import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Drawer,
  Empty,
  Input,
  Popconfirm,
  Segmented,
  Select,
  Spin,
  Switch,
  Upload,
  notification,
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ExportOutlined,
  FilePdfOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { list, save, changeStatus, remove } from "../../../controllers/cms/publications";
import { fetchIpAddress } from "../../../controllers/API";
import { API_BASE_URL, S3_BASE_URL, FRONT_SITE_URL } from "../../../config/constants";
import { generatePdfCover } from "./pdfCover";
import "./publications.css";

const MAX_PDF_MB = 45;

// Stored paths -> URLs (see API/app/models/publications.js)
const assetUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("./assets/")) return API_BASE_URL + path.slice(2);
  if (path.startsWith("./")) return S3_BASE_URL + path.slice(2);
  return FRONT_SITE_URL + path;
};

const monthLabel = (yyyyMm) => {
  const match = /^(\d{4})-(\d{2})$/.exec(yyyyMm || "");
  if (!match) return "";
  return new Date(Number(match[1]), Number(match[2]) - 1, 1).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
};

const formatSize = (bytes) => {
  if (!bytes) return "";
  return bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;
};

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const notify = (ok, message) =>
  notification.open({
    message: ok ? "Done" : "Oops!",
    description: message,
    placement: "topRight",
    icon: ok ? (
      <CheckCircleOutlined style={{ color: "green" }} />
    ) : (
      <InfoCircleOutlined style={{ color: "red" }} />
    ),
    duration: ok ? 2 : 5,
  });

const EMPTY_FORM = {
  _id: null,
  series: undefined,
  edition_month: currentMonth(),
  title: "",
  edition: "",
  tag: "",
  published: true,
  // title/edition follow series/month until the admin types their own
  titleTouched: false,
  editionTouched: false,
};

/* ---------------- Add / edit drawer ---------------- */

function EditionDrawer({ open, initial, seriesOptions, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [pdfFile, setPdfFile] = useState(null);
  const [pageCount, setPageCount] = useState(null);
  const [cover, setCover] = useState({ blob: null, preview: "", source: "none" });
  const [coverBusy, setCoverBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const isEdit = Boolean(initial?._id);

  useEffect(() => {
    if (!open) return;
    setPdfFile(null);
    setPageCount(null);
    setErrors({});
    if (initial?._id) {
      setForm({
        ...EMPTY_FORM,
        _id: initial._id,
        series: initial.series,
        edition_month: initial.edition_month,
        title: initial.title,
        edition: initial.edition,
        tag: initial.tag || "",
        published: initial.status !== "I",
        titleTouched: true,
        editionTouched: true,
      });
      setCover({ blob: null, preview: assetUrl(initial.cover), source: initial.cover ? "existing" : "none" });
    } else {
      // New edition -- `initial` may carry a series preset from the active tab
      const series = initial?.series;
      setForm({
        ...EMPTY_FORM,
        series,
        title: seriesOptions.find((s) => s.id === series)?.shortTitle || "",
        edition_month: currentMonth(),
        edition: `${monthLabel(currentMonth())} Edition`,
      });
      setCover({ blob: null, preview: "", source: "none" });
    }
  }, [open, initial, seriesOptions]);

  // Revoke object URLs we created for previews
  useEffect(() => {
    const url = cover.preview;
    return () => {
      if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
    };
  }, [cover.preview]);

  const update = (changes) => {
    setForm((prev) => {
      const next = { ...prev, ...changes };
      if (changes.series && !next.titleTouched) {
        next.title = seriesOptions.find((s) => s.id === changes.series)?.shortTitle || "";
      }
      if (changes.edition_month && !next.editionTouched) {
        next.edition = `${monthLabel(changes.edition_month)} Edition`;
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, ...Object.fromEntries(Object.keys(changes).map((k) => [k, ""])) }));
  };

  const applyFirstPageCover = useCallback(async (file) => {
    setCoverBusy(true);
    try {
      const { blob, pageCount: pages } = await generatePdfCover(file);
      setPageCount(pages);
      setCover({ blob, preview: URL.createObjectURL(blob), source: "generated" });
    } catch (error) {
      console.error("Cover generation failed", error);
      setErrors((prev) => ({
        ...prev,
        pdf: "This PDF couldn't be read. It may be damaged or password-protected. Please re-export it and try again.",
      }));
      setPdfFile(null);
    } finally {
      setCoverBusy(false);
    }
  }, []);

  const handlePdf = (file) => {
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setErrors((prev) => ({ ...prev, pdf: "Please choose a PDF file." }));
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_PDF_MB * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        pdf: `This PDF is ${formatSize(file.size)}; the limit is ${MAX_PDF_MB} MB. Export it again with a smaller file size and retry.`,
      }));
      return Upload.LIST_IGNORE;
    }
    setPdfFile(file);
    setErrors((prev) => ({ ...prev, pdf: "" }));
    // A new PDF gets a fresh cover unless the admin has chosen a custom one
    if (cover.source !== "custom") applyFirstPageCover(file);
    return false; // uploaded with the form on Save
  };

  const handleCustomCover = (file) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setErrors((prev) => ({ ...prev, cover: "Please choose a JPG, PNG or WebP image." }));
      return Upload.LIST_IGNORE;
    }
    setCover({ blob: file, preview: URL.createObjectURL(file), source: "custom" });
    setErrors((prev) => ({ ...prev, cover: "" }));
    return false;
  };

  const validate = () => {
    const next = {};
    if (!form.series) next.series = "Choose which publication this is.";
    if (!/^\d{4}-\d{2}$/.test(form.edition_month || "")) next.edition_month = "Choose the month and year.";
    if (!form.title.trim()) next.title = "Title is required.";
    if (!form.edition.trim()) next.edition = "Edition name is required.";
    if (!isEdit && !pdfFile) next.pdf = "Upload the PDF for this edition.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || coverBusy) return;
    setSaving(true);
    try {
      const data = new FormData();
      if (form._id) data.append("edit_id", form._id);
      data.append("series", form.series);
      data.append("edition_month", form.edition_month);
      data.append("title", form.title.trim());
      data.append("edition", form.edition.trim());
      data.append("tag", form.tag.trim());
      data.append("status", form.published ? "A" : "I");
      data.append("ipAddress", (await fetchIpAddress()) || "0.0.0.0");
      if (pdfFile) data.append("pdf", pdfFile, pdfFile.name);
      if (cover.blob) {
        data.append("cover", cover.blob, cover.source === "custom" ? cover.blob.name : "cover.jpg");
      }

      const res = await save(data);
      if (res.status) {
        notify(true, isEdit ? "Edition updated." : "Edition added.");
        onSaved();
      } else {
        notify(false, res.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const seriesName = seriesOptions.find((s) => s.id === form.series)?.name;

  return (
    <Drawer
      title={isEdit ? "Edit edition" : "Add a new edition"}
      open={open}
      onClose={saving ? undefined : onClose}
      width={620}
      destroyOnClose
      maskClosable={!saving}
      className="pubcms_drawer"
      footer={
        <div className="pubcms_drawer_footer">
          <span className="pubcms_drawer_hint">
            {form.published
              ? "Will appear on kchigh.com/publications as soon as you save."
              : "Saved as hidden: it won't appear on the website."}
          </span>
          <div>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleSave} loading={saving} disabled={coverBusy}>
              {saving ? (pdfFile ? "Uploading…" : "Saving…") : isEdit ? "Save changes" : "Add edition"}
            </Button>
          </div>
        </div>
      }
    >
      <div className="pubcms_form">
        <div className="pubcms_field">
          <label>
            Publication <span className="pubcms_req">*</span>
          </label>
          <Select
            placeholder="Choose a publication"
            value={form.series}
            onChange={(series) => update({ series })}
            options={seriesOptions.map((s) => ({ value: s.id, label: `${s.name} · ${s.cadence}` }))}
            status={errors.series ? "error" : undefined}
            style={{ width: "100%" }}
          />
          {errors.series && <p className="pubcms_error">{errors.series}</p>}
        </div>

        <div className="pubcms_row">
          <div className="pubcms_field">
            <label>
              Month &amp; year <span className="pubcms_req">*</span>
            </label>
            <input
              type="month"
              className={`form-control${errors.edition_month ? " is-invalid" : ""}`}
              value={form.edition_month}
              onChange={(e) => update({ edition_month: e.target.value })}
            />
            <p className="pubcms_help">Sets the order: newest editions show first.</p>
            {errors.edition_month && <p className="pubcms_error">{errors.edition_month}</p>}
          </div>
          <div className="pubcms_field">
            <label>Issue tag</label>
            <Input
              placeholder="e.g. Issue 20 (optional)"
              value={form.tag}
              onChange={(e) => update({ tag: e.target.value })}
              maxLength={30}
            />
            <p className="pubcms_help">Shown as a small badge on the cover.</p>
          </div>
        </div>

        <div className="pubcms_row">
          <div className="pubcms_field">
            <label>
              Title <span className="pubcms_req">*</span>
            </label>
            <Input
              value={form.title}
              onChange={(e) => update({ title: e.target.value, titleTouched: true })}
              status={errors.title ? "error" : undefined}
              placeholder={seriesName || "e.g. MS Express"}
            />
            {errors.title && <p className="pubcms_error">{errors.title}</p>}
          </div>
          <div className="pubcms_field">
            <label>
              Edition name <span className="pubcms_req">*</span>
            </label>
            <Input
              value={form.edition}
              onChange={(e) => update({ edition: e.target.value, editionTouched: true })}
              status={errors.edition ? "error" : undefined}
              placeholder="e.g. September 2026 Edition"
            />
            {errors.edition && <p className="pubcms_error">{errors.edition}</p>}
          </div>
        </div>

        <div className="pubcms_field">
          <label>
            PDF {!isEdit && <span className="pubcms_req">*</span>}
          </label>
          {isEdit && !pdfFile && initial?.file && (
            <div className="pubcms_current_file">
              <FilePdfOutlined />
              <a href={assetUrl(initial.file)} target="_blank" rel="noopener noreferrer">
                Current PDF
              </a>
              {initial.file_size ? <span>{formatSize(initial.file_size)}</span> : null}
              <span className="pubcms_current_file_note">Upload a new one below to replace it.</span>
            </div>
          )}
          <Upload.Dragger
            accept="application/pdf,.pdf"
            maxCount={1}
            showUploadList={false}
            beforeUpload={handlePdf}
            className="pubcms_dragger"
          >
            {pdfFile ? (
              <div className="pubcms_dragger_file">
                <FilePdfOutlined className="pubcms_dragger_icon" />
                <div>
                  <strong>{pdfFile.name}</strong>
                  <span>
                    {formatSize(pdfFile.size)}
                    {pageCount ? ` · ${pageCount} pages` : ""} · click or drop to choose a different file
                  </span>
                </div>
              </div>
            ) : (
              <div className="pubcms_dragger_empty">
                <FilePdfOutlined className="pubcms_dragger_icon" />
                <strong>{isEdit ? "Replace the PDF" : "Click or drop the PDF here"}</strong>
                <span>Up to {MAX_PDF_MB} MB</span>
              </div>
            )}
          </Upload.Dragger>
          {errors.pdf && <p className="pubcms_error">{errors.pdf}</p>}
        </div>

        <div className="pubcms_field">
          <label>Cover</label>
          <div className="pubcms_cover_row">
            <div className="pubcms_cover_preview">
              {coverBusy ? (
                <Spin tip="Reading page 1…" />
              ) : cover.preview ? (
                <img src={cover.preview} alt="Cover preview" />
              ) : (
                <div className="pubcms_cover_placeholder">
                  <PictureOutlined />
                </div>
              )}
            </div>
            <div className="pubcms_cover_actions">
              <p className="pubcms_help">
                {cover.source === "generated" && "Using the PDF's first page as the cover."}
                {cover.source === "custom" && "Using your uploaded image as the cover."}
                {cover.source === "existing" && "Current cover. Uploading a new PDF will refresh it from page 1."}
                {cover.source === "none" && "The first page of the PDF is used automatically once you add it."}
              </p>
              <Upload accept="image/jpeg,image/png,image/webp" showUploadList={false} beforeUpload={handleCustomCover}>
                <Button icon={<PictureOutlined />}>Upload a custom cover</Button>
              </Upload>
              {pdfFile && cover.source === "custom" && (
                <Button icon={<ReloadOutlined />} onClick={() => applyFirstPageCover(pdfFile)}>
                  Use the PDF's first page instead
                </Button>
              )}
              {errors.cover && <p className="pubcms_error">{errors.cover}</p>}
            </div>
          </div>
        </div>

        <div className="pubcms_field pubcms_publish_row">
          <Switch checked={form.published} onChange={(published) => update({ published })} />
          <div>
            <strong>{form.published ? "Published" : "Hidden"}</strong>
            <p className="pubcms_help">
              Hidden editions stay in the CMS but aren't shown on the website.
            </p>
          </div>
        </div>
      </div>
    </Drawer>
  );
}

/* ---------------- Edition card ---------------- */

function EditionCard({ edition, seriesName, onEdit, onToggle, onDelete, busy }) {
  const hidden = edition.status === "I";
  return (
    <div className={`pubcms_card${hidden ? " is_hidden" : ""}`}>
      <div className="pubcms_card_cover" onClick={() => onEdit(edition)} role="button" tabIndex={0}>
        {edition.cover ? (
          <img src={assetUrl(edition.cover)} alt={`${edition.title} cover`} loading="lazy" />
        ) : (
          <div className="pubcms_cover_placeholder">
            <FilePdfOutlined />
          </div>
        )}
        {edition.tag && <span className="pubcms_card_tag">{edition.tag}</span>}
        {hidden && <span className="pubcms_card_hidden">Hidden</span>}
      </div>
      <div className="pubcms_card_body">
        <span className="pubcms_card_series">{seriesName}</span>
        <h3>{edition.title}</h3>
        <p>{edition.edition}</p>
        <span className="pubcms_card_meta">
          {monthLabel(edition.edition_month)}
          {edition.file_size ? ` · ${formatSize(edition.file_size)}` : ""}
        </span>
      </div>
      <div className="pubcms_card_footer">
        <label className="pubcms_card_switch">
          <Switch size="small" checked={!hidden} loading={busy} onChange={() => onToggle(edition)} />
          <span>{hidden ? "Hidden" : "Published"}</span>
        </label>
        <div className="pubcms_card_actions">
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => onEdit(edition)} title="Edit" />
          <Button
            size="small"
            type="text"
            icon={<ExportOutlined />}
            href={assetUrl(edition.file)}
            target="_blank"
            title="Open PDF"
          />
          <Popconfirm
            title="Delete this edition?"
            description="The PDF and cover will be removed permanently."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(edition)}
          >
            <Button size="small" type="text" danger icon={<DeleteOutlined />} title="Delete" />
          </Popconfirm>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Page ---------------- */

const Publications = () => {
  const [editions, setEditions] = useState([]);
  const [seriesOptions, setSeriesOptions] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState({ open: false, edition: null });
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await list();
    if (res.status) {
      setEditions(res.result || []);
      setSeriesOptions(res.series || []);
    } else {
      notify(false, res.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    document.title = "KC High Super Admin || Publications";
    load();
  }, [load]);

  const seriesName = useCallback(
    (id) => seriesOptions.find((s) => s.id === id)?.name || id,
    [seriesOptions]
  );

  const visible = useMemo(
    () => (filter === "all" ? editions : editions.filter((e) => e.series === filter)),
    [editions, filter]
  );

  const segments = [
    { value: "all", label: `All (${editions.length})` },
    ...seriesOptions.map((s) => ({
      value: s.id,
      label: `${s.name} (${editions.filter((e) => e.series === s.id).length})`,
    })),
  ];

  const handleToggle = async (edition) => {
    setBusyId(edition._id);
    const status = edition.status === "I" ? "A" : "I";
    const res = await changeStatus({ id: edition._id, status, ipAddress: await fetchIpAddress() });
    if (res.status) {
      setEditions((prev) => prev.map((e) => (e._id === edition._id ? { ...e, status } : e)));
    } else {
      notify(false, res.message);
    }
    setBusyId(null);
  };

  const handleDelete = async (edition) => {
    const res = await remove({ id: edition._id });
    if (res.status) {
      setEditions((prev) => prev.filter((e) => e._id !== edition._id));
      notify(true, "Edition deleted.");
    } else {
      notify(false, res.message);
    }
  };

  const openAdd = () => {
    const preset = filter !== "all" ? { series: filter } : null;
    setDrawer({ open: true, edition: preset });
  };

  return (
    <div className="container-fluid pubcms">
      <div className="pubcms_header">
        <div>
          <span className="pubcms_kicker">At KC...</span>
          <h1>Publications</h1>
          <p>
            Add new editions of the school's publications. Published editions appear on{" "}
            <a href={`${FRONT_SITE_URL}/publications`} target="_blank" rel="noopener noreferrer">
              kchigh.com/publications
            </a>{" "}
            straight away, newest first.
          </p>
        </div>
        <Button type="primary" size="large" icon={<PlusOutlined />} onClick={openAdd}>
          Add edition
        </Button>
      </div>

      {seriesOptions.length > 0 && (
        <Segmented
          className="pubcms_tabs"
          options={segments}
          value={filter}
          onChange={setFilter}
        />
      )}

      {loading ? (
        <div className="pubcms_loading">
          <Spin size="large" />
        </div>
      ) : visible.length === 0 ? (
        <Empty
          className="pubcms_empty"
          description={
            filter === "all"
              ? "No editions yet."
              : `No ${seriesName(filter)} editions yet.`
          }
        >
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Add the first one
          </Button>
        </Empty>
      ) : (
        <div className="pubcms_grid">
          {visible.map((edition) => (
            <EditionCard
              key={edition._id}
              edition={edition}
              seriesName={seriesName(edition.series)}
              onEdit={(e) => setDrawer({ open: true, edition: e })}
              onToggle={handleToggle}
              onDelete={handleDelete}
              busy={busyId === edition._id}
            />
          ))}
        </div>
      )}

      <EditionDrawer
        open={drawer.open}
        initial={drawer.edition}
        seriesOptions={seriesOptions}
        onClose={() => setDrawer({ open: false, edition: null })}
        onSaved={() => {
          setDrawer({ open: false, edition: null });
          load();
        }}
      />
    </div>
  );
};

export default Publications;
